import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";

// The pilots-domain tasks of a pilot's team (GET /prospects/{id}/pilot/tasks). `enabled` is false for someone without `tasks:view` (the route would answer
// 403): the list is then simply empty. status: loading | ready | error. A late answer for a previous prospect or an older request is dropped (`seq`),
// and a failed background refresh keeps what is on screen.
export default function usePilotTeamTasks(prospectId, enabled = true) {
  const [state, setState] = useState({ status: enabled ? "loading" : "ready", items: [] });
  const seq = useRef(0);

  const load = useCallback(async ({ silent = false } = {}) => {
    const mine = ++seq.current;
    if (!enabled) { setState({ status: "ready", items: [] }); return; }
    if (!silent) setState((s) => ({ status: "loading", items: s.items }));
    try {
      const { data } = await api.get(`/prospects/${prospectId}/pilot/tasks`);
      if (mine === seq.current) setState({ status: "ready", items: data.items || [], truncated: !!data.truncated });
    } catch (e) {
      if (mine !== seq.current) return;
      setState((s) => (silent && s.status === "ready" ? s : { status: "error", items: [] }));
    }
  }, [prospectId, enabled]);

  useEffect(() => {
    load();
    return () => { seq.current += 1; };
  }, [load]);

  // The result of a local change (a task just patched) is newer than any request still in flight.
  const replaceItem = useCallback((task) => {
    seq.current += 1;
    setState((s) => ({ ...s, items: s.items.map((t) => (t.id === task.id ? { ...t, ...task } : t)) }));
  }, []);

  return { teamTasks: state, reloadTeamTasks: load, replaceTeamTask: replaceItem };
}
